import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Swords } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { anchorPoint, TABLE_CENTER } from "../fxAnchors";
import { ROLES } from "../gameData";
import type { FxEffect } from "../useGameDeltas";
import { useGameDeltas } from "../useGameDeltas";
import type { GameState, Role } from "../types";

/**
 * The animation layer — VEIL's replacement for the log it deliberately does not
 * have. Coins fly between the seats that gained and lost them, a card comes out
 * of anyone who claims a role, a challenge is held for a beat before its
 * verdict lands, and a lost Influence is shown at the seat that lost it.
 *
 * Two hard rules govern this file:
 *
 *  1. It NEVER gates incoming state. `setGame` keeps applying snapshots the
 *     instant they arrive; a bot's 700ms alarm can land mid-beat and the table
 *     underneath simply updates. Everything here is presentational, owned by
 *     local state, bounded by a hard timeout, and dismissable with a tap.
 *  2. A bluff and a truthful claim animate IDENTICALLY. The client holds its
 *     own hand and could compute the difference; any tell would leak it.
 */

interface Props {
  game: GameState;
  meId: string;
}

/** The kinds `.fx-layer` actually paints. `challenge` and `callout` are beat
 *  material and `victory` belongs to App's ink wipe, so neither is ever held
 *  below: parking an effect that has no renderer would park it forever, because
 *  nothing would ever call `onDone` to retire it. */
const PAINTED: ReadonlySet<FxEffect["kind"]> = new Set([
  "coin",
  "claim",
  "reveal",
  "out",
  "redraw"
]);

/** One duel, identified by the only three fields that survive the mask on
 *  pendingLoss. Both halves of a doubt therefore produce the SAME key, which is
 *  what lets the pending stage hand over to its verdict without unmounting. */
function duelKey(effect: Extract<FxEffect, { kind: "challenge" | "callout" }>) {
  return `${effect.claimantId}:${effect.challengerId}:${effect.role}`;
}

interface ChallengeBeat {
  key: string;
  role: Role;
  /** null while the accusation is still hanging — which is every doubt, at
   *  first. Neither verdict is the table's to know until somebody surrenders a
   *  card, so the stage runs with an empty verdict slot and the court centre
   *  carries the wait. */
  proved: boolean | null;
  /** You are the claimant or the challenger — hold the full beat. */
  full: boolean;
  caller: string;
}

export function TableFx({ game, meId }: Props) {
  const reduced = useReducedMotion() ?? false;
  const layerRef = useRef<HTMLDivElement | null>(null);
  const { effects, dismiss, flush } = useGameDeltas(game, meId);
  const [beat, setBeat] = useState<ChallengeBeat | null>(null);
  /** Flourishes that arrived while the stage owned the screen, waiting it out. */
  const [held, setHeld] = useState<FxEffect[]>([]);
  const beatTimer = useRef<number | null>(null);
  const gameOver = game.phase === "game_over";

  /** A flourish can retire from either queue, and never knows which it is in. */
  const retire = useCallback(
    (id: string) => {
      setHeld((queue) =>
        queue.some((effect) => effect.id === id) ? queue.filter((effect) => effect.id !== id) : queue
      );
      dismiss(id);
    },
    [dismiss]
  );

  const nameOf = useCallback(
    (playerId: string) => game.players.find((player) => player.id === playerId)?.name ?? "A player",
    [game.players]
  );

  const clearBeat = useCallback(() => {
    if (beatTimer.current !== null) {
      window.clearTimeout(beatTimer.current);
      beatTimer.current = null;
    }
    setBeat(null);
  }, []);

  useEffect(() => () => clearBeat(), [clearBeat]);

  // Promote a challenge delta into a locally-owned beat. Local, not derived:
  // the server resolved the challenge in the same message that opened it, so
  // the suspense is entirely fabricated here and has to survive every snapshot
  // that lands while it plays.
  useEffect(() => {
    // Two beats share one stage. A `callout` is the accusation with no verdict
    // attached; the `challenge` that follows it once the loss clears carries the
    // verdict. Every doubt now goes through both, in that order, whichever way
    // it went — a proven claim that skipped straight to the second was itself
    // the tell that made the whole secret cosmetic.
    const challenge = effects.find(
      (effect): effect is Extract<FxEffect, { kind: "challenge" | "callout" }> =>
        effect.kind === "challenge" || effect.kind === "callout"
    );
    if (!challenge) return;
    dismiss(challenge.id);

    const proved = challenge.kind === "challenge" ? challenge.proved : null;
    const mine = challenge.claimantId === meId;
    const theirs = challenge.challengerId === meId;
    const full = mine || theirs;
    const caller = mine
      ? `${nameOf(challenge.challengerId)} calls you out`
      : theirs
        ? `You call out ${nameOf(challenge.claimantId)}`
        : `${nameOf(challenge.challengerId)} calls out ${nameOf(challenge.claimantId)}`;

    // Keyed by the duel and not by the effect, so a verdict landing on a still
    // running callout REPLACES it in place rather than cross-fading with it:
    // a bot bluffer surrenders one 500ms alarm after the callout opens, so the
    // pending beat is pre-empted more often than it is allowed to expire, and
    // two full-inset scrims composing at ~0.86 with two cards in the same grid
    // cell was the routine case rather than the rare one. This also IS the
    // yield — the 1100ms timer below is cleared, not waited out.
    setBeat({ key: duelKey(challenge), role: challenge.role, proved, full, caller });
    if (beatTimer.current !== null) window.clearTimeout(beatTimer.current);
    beatTimer.current = window.setTimeout(
      () => {
        beatTimer.current = null;
        setBeat(null);
      },
      // A pending callout is deliberately the short one, and the same length for
      // everyone: there is no verdict to land on, and the court-centre panel
      // takes over the wait the moment this clears.
      proved === null ? 1100 : reduced ? 1000 : full ? 1750 : 1100
    );
    // The cleanup deliberately lives in the unmount effect above: returning a
    // clear() from here would kill the running beat the moment `effects`
    // changes for an unrelated reason.
  }, [effects, dismiss, meId, nameOf, reduced]);

  // Pull anything the stage would bury OUT of the live queue instead of merely
  // skipping it at render time. Skipping alone was not enough: the queue is
  // capped with `.slice(-MAX_LIVE)`, which drops the OLDEST first, and a waiting
  // flourish is by definition the oldest — a burst of bot turns could quietly
  // evict the very reveal the verdict was about. Held effects re-mount when the
  // stage clears, however it clears — its own timeout or an impatient tap — and
  // measure their anchors then, so a table that re-flowed while they waited is
  // still aimed at correctly. The queue cannot grow without bound because a beat
  // cannot outlive its own hard timeout.
  useEffect(() => {
    if (!beat) return;
    const buried = effects.filter((effect) => PAINTED.has(effect.kind));
    if (!buried.length) return;
    setHeld((queue) => {
      const known = new Set(queue.map((effect) => effect.id));
      return [...queue, ...buried.filter((effect) => !known.has(effect.id))];
    });
    for (const effect of buried) dismiss(effect.id);
  }, [beat, effects, dismiss]);

  // The victory sequence owns the screen from here. Anything still queued would
  // only fight the ink wipe, and a stale beat must never outlive the game.
  useEffect(() => {
    if (!gameOver) return;
    clearBeat();
    flush();
    setHeld([]);
  }, [gameOver, clearBeat, flush]);

  return (
    <>
      <div className="fx-layer" ref={layerRef} aria-hidden="true">
        {/* The challenge stage is a full-inset scrim two stacking contexts above
            this layer, so ANY flourish painted underneath it is simply never
            seen — and a verdict now lands on the same snapshot pair as the coin
            claw-back, the surrendered card and the OUT stamp it explains. All of
            them wait, rather than being spent on a covered screen. */}
        {(gameOver || beat ? [] : [...held, ...effects]).map((effect) => {
          switch (effect.kind) {
            case "coin":
              return (
                <CoinFlight
                  key={effect.id}
                  effect={effect}
                  layerRef={layerRef}
                  reduced={reduced}
                  onDone={retire}
                />
              );
            case "claim":
              return (
                <ClaimCard
                  key={effect.id}
                  effect={effect}
                  layerRef={layerRef}
                  reduced={reduced}
                  onDone={retire}
                />
              );
            case "reveal":
              return (
                <RevealCard
                  key={effect.id}
                  effect={effect}
                  layerRef={layerRef}
                  reduced={reduced}
                  onDone={retire}
                />
              );
            case "out":
              return (
                <OutStamp
                  key={effect.id}
                  effect={effect}
                  layerRef={layerRef}
                  reduced={reduced}
                  onDone={retire}
                />
              );
            case "redraw":
              return (
                <RedrawSwap
                  key={effect.id}
                  effect={effect}
                  layerRef={layerRef}
                  reduced={reduced}
                  onDone={retire}
                />
              );
            default:
              return null;
          }
        })}
      </div>

      {/* mode="wait" is the backstop for the one handoff the duel key cannot
          absorb: two DIFFERENT doubts back to back. Never two scrims at once. */}
      <AnimatePresence mode="wait">
        {beat && <ChallengeStage key={beat.key} beat={beat} reduced={reduced} onDismiss={clearBeat} />}
      </AnimatePresence>
    </>
  );
}

/* ------------------------------------------------------------------ parts -- */

type LayerRef = React.MutableRefObject<HTMLDivElement | null>;

interface FxProps<T extends FxEffect> {
  effect: T;
  layerRef: LayerRef;
  reduced: boolean;
  onDone: (id: string) => void;
}

interface Placement {
  x: number;
  y: number;
  /** How far above the anchor this sprite may travel before it leaves the screen. */
  rise: number;
}

/**
 * Measure an anchor once, at fire time, in the layer's own coordinate space,
 * and work out how much room the sprite actually has around it.
 *
 * Both clamps are load-bearing on a phone. `half.x` keeps a card hung off the
 * outermost seat (~39px from the edge) out of `.app-shell`'s clip, and `rise`
 * collapses toward 0 for the seats at the top of the arc, which sit barely a
 * card's height below the header — an unbounded rise decapitates them.
 */
function usePlacement(
  anchor: string,
  layerRef: LayerRef,
  half: { x: number; y: number },
  wantRise = 0
): Placement | null {
  return useMemo(() => {
    const bounds = layerRef.current?.getBoundingClientRect() ?? null;
    const point = anchorPoint(anchor, bounds);
    if (!point || !bounds) return null;
    const header = document.querySelector(".game-header")?.getBoundingClientRect();
    const ceiling = (header ? header.bottom - bounds.top : 0) + 6;
    const edge = half.x + 4;
    return {
      x: Math.min(Math.max(point.x, edge), Math.max(edge, bounds.width - edge)),
      y: Math.max(point.y, ceiling + half.y),
      rise: Math.max(0, Math.min(wantRise, point.y - half.y - ceiling))
    };
  }, [anchor, layerRef, half.x, half.y, wantRise]);
}

/** Nothing to aim at (a seat that just left, a first paint) — retire quietly. */
function useAbortIfUnanchored(missing: boolean, id: string, onDone: (id: string) => void) {
  useEffect(() => {
    if (missing) onDone(id);
  }, [missing, id, onDone]);
}

function CoinFlight({ effect, layerRef, reduced, onDone }: FxProps<Extract<FxEffect, { kind: "coin" }>>) {
  const plan = useMemo(() => {
    const origin = layerRef.current?.getBoundingClientRect() ?? null;
    const from = anchorPoint(effect.from, origin);
    const to = anchorPoint(effect.to, origin);
    if (!from || !to) return null;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy) || 1;
    // Perpendicular unit vector: the arc bows out to one side of the straight
    // line, so a fistful of coins reads as a throw rather than a laser.
    const nx = -dy / length;
    const ny = dx / length;
    return {
      from,
      to,
      coins: Array.from({ length: effect.count }, (_, index) => {
        const lift = Math.min(70, length * 0.28) + index * 5;
        return { index, dx, dy, mx: dx * 0.5 + nx * lift, my: dy * 0.5 + ny * lift };
      })
    };
  }, [effect, layerRef]);

  useAbortIfUnanchored(!plan, effect.id, onDone);
  if (!plan) return null;

  if (reduced) {
    return (
      <motion.span
        className="fx-pulse"
        style={{ left: plan.to.x, top: plan.to.y }}
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: 1.7, opacity: [0, 0.9, 0] }}
        transition={{ duration: 0.3 }}
        onAnimationComplete={() => onDone(effect.id)}
      />
    );
  }

  return (
    <>
      {plan.coins.map((coin) => (
        <motion.span
          key={coin.index}
          className="fx-coin"
          style={{ left: plan.from.x, top: plan.from.y }}
          initial={{ x: 0, y: 0, scale: 0.4, opacity: 0 }}
          animate={{
            x: [0, coin.mx, coin.dx],
            y: [0, coin.my, coin.dy],
            scale: [0.4, 1.05, 0.55],
            opacity: [0, 1, 0],
            rotate: [0, 200, 380]
          }}
          transition={{
            duration: 0.52,
            delay: coin.index * 0.055,
            times: [0, 0.55, 1],
            ease: [0.22, 0.9, 0.28, 1]
          }}
          onAnimationComplete={() => {
            if (coin.index === plan.coins.length - 1) onDone(effect.id);
          }}
        />
      ))}
    </>
  );
}

const CARD_HALF = { x: 34, y: 46 };

function ClaimCard({ effect, layerRef, reduced, onDone }: FxProps<Extract<FxEffect, { kind: "claim" }>>) {
  const place = usePlacement(effect.anchor, layerRef, CARD_HALF, 70);
  const role = ROLES[effect.role];
  useAbortIfUnanchored(!place, effect.id, onDone);
  if (!place) return null;
  const top = place.rise;

  return (
    <motion.span
      className="fx-claim-card"
      style={{ left: place.x, top: place.y, "--role-color": role.color } as React.CSSProperties}
      initial={reduced ? { opacity: 0, y: -top } : { y: 8, scale: 0.14, rotate: -18, opacity: 0 }}
      animate={
        reduced
          ? { opacity: [0, 1, 1, 0], y: -top }
          : {
              y: [8, -top, -top * 0.9, -top * 0.9, -top - 14],
              scale: [0.14, 1.06, 1, 1, 0.74],
              rotate: [-18, -5, -4, -4, -4],
              opacity: [0, 1, 1, 1, 0]
            }
      }
      transition={
        reduced
          ? { duration: 0.9, times: [0, 0.18, 0.76, 1] }
          : {
              duration: 1.5,
              times: [0, 0.22, 0.31, 0.8, 1],
              ease: ["easeOut", "easeOut", "linear", "easeIn"]
            }
      }
      onAnimationComplete={() => onDone(effect.id)}
    >
      <img src={role.image} alt="" draggable={false} />
      <b>{role.name}</b>
    </motion.span>
  );
}

function RevealCard({ effect, layerRef, reduced, onDone }: FxProps<Extract<FxEffect, { kind: "reveal" }>>) {
  const place = usePlacement(effect.anchor, layerRef, CARD_HALF, 46);
  const role = ROLES[effect.role];
  useAbortIfUnanchored(!place, effect.id, onDone);
  if (!place) return null;
  const top = place.rise;

  return (
    <motion.span
      className="fx-claim-card fx-claim-card--lost"
      style={{ left: place.x, top: place.y, "--role-color": role.color } as React.CSSProperties}
      initial={reduced ? { opacity: 0, y: -top } : { y: 0, scale: 0.32, rotate: 0, opacity: 0 }}
      animate={
        reduced
          ? { opacity: [0, 1, 1, 0], y: -top }
          : {
              y: [0, -top, -top * 0.9, -top * 0.34],
              scale: [0.32, 1.05, 1, 0.86],
              rotate: [0, 5, 4, 13],
              opacity: [0, 1, 1, 0]
            }
      }
      transition={
        reduced
          ? { duration: 0.9, times: [0, 0.18, 0.76, 1] }
          : { duration: 1.35, times: [0, 0.2, 0.72, 1], ease: ["easeOut", "linear", "easeIn"] }
      }
      onAnimationComplete={() => onDone(effect.id)}
    >
      <img src={role.image} alt="" draggable={false} />
      <b>{role.name}</b>
    </motion.span>
  );
}

function OutStamp({ effect, layerRef, reduced, onDone }: FxProps<Extract<FxEffect, { kind: "out" }>>) {
  const place = usePlacement(effect.anchor, layerRef, { x: 44, y: 18 });
  useAbortIfUnanchored(!place, effect.id, onDone);
  if (!place) return null;

  return (
    <motion.span
      className="fx-out"
      style={{ left: place.x, top: place.y }}
      initial={reduced ? { opacity: 0, rotate: -9 } : { scale: 2.4, opacity: 0, rotate: -16 }}
      animate={
        reduced
          ? { opacity: [0, 1, 1, 0], rotate: -9 }
          : { scale: [2.4, 1, 1, 1.12], opacity: [0, 1, 1, 0], rotate: [-16, -9, -9, -9] }
      }
      transition={
        reduced
          ? { duration: 0.9, times: [0, 0.18, 0.76, 1] }
          : { duration: 1.35, times: [0, 0.13, 0.78, 1], ease: ["easeOut", "linear", "easeIn"] }
      }
      onAnimationComplete={() => onDone(effect.id)}
    >
      OUT
    </motion.span>
  );
}

/**
 * The proven card going home. A claim that survives a doubt is not just
 * vindicated — the card is returned to the Court deck and a fresh one is dealt
 * in its place, which is why a proven Duke can be doubted again a turn later and
 * why bluffing the same role twice is not automatically safe. None of that is
 * legible from the snapshot, so the swap is shown: the proven card flies into
 * the table centre (which doubles as the deck) and a replacement flies back out.
 *
 * The replacement is face up ONLY in your own hand. Every other seat sees a
 * blank card come out, because that is exactly what they are entitled to know.
 */
function RedrawSwap({ effect, layerRef, reduced, onDone }: FxProps<Extract<FxEffect, { kind: "redraw" }>>) {
  const place = usePlacement(effect.anchor, layerRef, CARD_HALF);
  const court = useMemo(
    () => anchorPoint(TABLE_CENTER, layerRef.current?.getBoundingClientRect() ?? null),
    [layerRef]
  );
  const proven = ROLES[effect.role];
  const drawn = effect.newRole ? ROLES[effect.newRole] : null;
  useAbortIfUnanchored(!place || !court, effect.id, onDone);
  if (!place || !court) return null;

  const dx = court.x - place.x;
  const dy = court.y - place.y;
  const seat = { left: place.x, top: place.y };
  // A face-down replacement has no role colour to take; leaving the property
  // unset lets the stylesheet paint its own card back rather than inheriting
  // whatever colour happened to be on an ancestor.
  const drawnStyle = drawn
    ? ({ ...seat, "--role-color": drawn.color } as React.CSSProperties)
    : (seat as React.CSSProperties);

  if (reduced) {
    return (
      <>
        <motion.span
          className="fx-redraw"
          style={{ ...seat, "--role-color": proven.color } as React.CSSProperties}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 0.5, times: [0, 0.35, 1] }}
        >
          <span className="fx-redraw__card">
            <img src={proven.image} alt="" draggable={false} />
            <b>{proven.name}</b>
          </span>
        </motion.span>
        <motion.span
          className="fx-redraw fx-redraw--drawn"
          style={drawnStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 0.55, delay: 0.38, times: [0, 0.3, 1] }}
          onAnimationComplete={() => onDone(effect.id)}
        >
          <span className="fx-redraw__card">
            {drawn ? (
              <>
                <img src={drawn.image} alt="" draggable={false} />
                <b>{drawn.name}</b>
              </>
            ) : (
              <b>V</b>
            )}
          </span>
        </motion.span>
      </>
    );
  }

  return (
    <>
      <motion.span
        className="fx-redraw"
        style={{ ...seat, "--role-color": proven.color } as React.CSSProperties}
        initial={{ x: 0, y: 0, scale: 0.9, rotate: 0, opacity: 0 }}
        animate={{
          x: [0, dx * 0.55, dx],
          y: [0, dy * 0.55 - 26, dy],
          scale: [0.9, 1, 0.3],
          rotate: [0, 9, 26],
          opacity: [0, 1, 0]
        }}
        transition={{ duration: 0.66, times: [0, 0.42, 1], ease: ["easeOut", "easeIn"] }}
      >
        <span className="fx-redraw__card">
          <img src={proven.image} alt="" draggable={false} />
          <b>{proven.name}</b>
        </span>
      </motion.span>

      <motion.span
        className="fx-redraw fx-redraw--drawn"
        style={drawnStyle}
        initial={{ x: dx, y: dy, scale: 0.3, rotate: -20, opacity: 0 }}
        animate={{
          x: [dx, dx * 0.5, 0, 0],
          y: [dy, dy * 0.5 - 22, 0, 0],
          scale: [0.3, 0.94, 1.04, 0.92],
          rotate: [-20, -9, -3, -3],
          opacity: [0, 1, 1, 0]
        }}
        transition={{
          duration: 0.92,
          delay: 0.5,
          times: [0, 0.4, 0.7, 1],
          ease: ["easeOut", "easeOut", "easeIn"]
        }}
        // Always the last half to finish, so it alone retires the effect.
        onAnimationComplete={() => onDone(effect.id)}
      >
        <span className="fx-redraw__card">
          {drawn ? (
            <>
              <img src={drawn.image} alt="" draggable={false} />
              <b>{drawn.name}</b>
            </>
          ) : (
            <b>V</b>
          )}
        </span>
      </motion.span>
    </>
  );
}

/**
 * The held beat. The claimed card lands centre-screen ringed in coral, shakes
 * while the table decides, and only then does the verdict stamp fall.
 *
 * The verdict element is mounted from the start at opacity 0 so it reserves its
 * row in the grid — otherwise the card would jump when the stamp arrives.
 *
 * PENDING is the same stage with the verdict withheld. NEITHER verdict is the
 * table's to know until somebody surrenders a card, so every doubt opens here:
 * the claimed card shakes, and the slot the stamp would occupy holds a muted
 * line instead. Rule 2 at the top of this file is what forces it — anything that
 * resolved faster or louder for a bluff than for a proven claim would be a tell,
 * so the pending stage is identical for every viewer and for both outcomes.
 *
 * The verdict then arrives IN PLACE, on the same mounted stage: `beat.key` is
 * the duel, not the effect, so the card never leaves the screen and two scrims
 * never overlap. That is also why the stamp is timed by a framer `delay` rather
 * than a state flip — a flag that had already fired for the pending line would
 * paint the stamp fully fallen in the first frame after the handoff.
 */
function ChallengeStage({
  beat,
  reduced,
  onDismiss
}: {
  beat: ChallengeBeat;
  reduced: boolean;
  onDismiss: () => void;
}) {
  const role = ROLES[beat.role];
  const pending = beat.proved === null;
  /** Seconds — this is a transition delay, not a timer. */
  const stamp = pending ? 0.34 : reduced ? 0.48 : beat.full ? 0.9 : 0.62;

  useEffect(() => {
    // The buzz is the stamp's punctuation. A pending callout has no stamp, and a
    // haptic on one would be a second channel saying "bluff".
    if (pending) return;
    const timer = window.setTimeout(() => navigator.vibrate?.([30, 40, 60]), stamp * 1000);
    return () => window.clearTimeout(timer);
  }, [pending, stamp]);

  return (
    <motion.div
      className={pending ? "challenge-stage challenge-stage--pending" : "challenge-stage"}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduced ? 0.12 : 0.2 }}
      // A tap always cuts it short. Never trap an impatient player behind a
      // flourish — the real game state is already live underneath.
      onPointerDown={onDismiss}
    >
      <motion.span
        className="challenge-stage__caller"
        initial={reduced ? { opacity: 0 } : { y: -12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.05, duration: 0.2 }}
      >
        <Swords size={15} strokeWidth={2.6} />
        {beat.caller}
      </motion.span>

      <motion.span
        initial={reduced ? { opacity: 0 } : { scale: 0.55, opacity: 0 }}
        animate={reduced ? { opacity: 1 } : { scale: [0.55, 1.08, 1], opacity: 1 }}
        transition={reduced ? { duration: 0.16 } : { duration: 0.28, times: [0, 0.72, 1] }}
      >
        <motion.span
          className="challenge-stage__card"
          style={{ "--role-color": role.color } as React.CSSProperties}
          animate={
            reduced
              ? undefined
              : { x: [0, -7, 6, -5, 4, -2, 0], rotate: [0, -1.8, 1.5, -1.1, 0.8, -0.4, 0] }
          }
          transition={
            reduced ? undefined : { delay: 0.16, duration: 0.34, repeat: 2, ease: "easeInOut" }
          }
        >
          <img src={role.image} alt="" draggable={false} />
          <b>{role.name}</b>
        </motion.span>
      </motion.span>

      {/* Both occupants of the verdict row are mounted from the first frame at
          opacity 0, for the same reason: the row has to be reserved or the card
          above it jumps when the slot finally fills. */}
      {pending ? (
        <motion.span
          className="challenge-stage__pending"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: stamp, duration: reduced ? 0.12 : 0.24 }}
        >
          Choosing an Influence…
        </motion.span>
      ) : (
        <motion.span
          className={
            beat.proved
              ? "challenge-stage__verdict"
              : "challenge-stage__verdict challenge-stage__verdict--bluff"
          }
          initial={{ scale: 2.2, opacity: 0, rotate: -8 }}
          animate={{ scale: 1, opacity: 1, rotate: -8 }}
          transition={
            reduced
              ? { delay: stamp, duration: 0.16 }
              : { delay: stamp, type: "spring", stiffness: 520, damping: 14 }
          }
        >
          {beat.proved ? "Proven" : "Bluff"}
        </motion.span>
      )}
    </motion.div>
  );
}
